import { Injectable, inject } from '@angular/core'

import { Apollo } from 'apollo-angular'
import type { Observable } from 'rxjs'
import { catchError, map, of, throwError } from 'rxjs'

import type { DateRange } from '../../components/filter-group/components/filters/components/components/filter-date-range'
import type {
  ArticleSearchParams,
  ArticleSearchSorting,
  ArticleSearchWhereClause,
  ArticleSearchWhereConditions,
  ArticleType,
  FetchPolicy,
  IndexItem,
} from '../../types/types'
import { ARTICLE_TYPE_BACKEND_TITLES, ArticleOptions } from '../../types/types'
import type { SortParameter } from '../api.service'
import { ApiService } from '../api.service'
import { completeDataOrThrow } from '../complete-data.operator'
import { SEARCH_ARTICLES } from '../queries'
import type {
  ArticleDetailsDto,
  ArticlePurchaseOptions,
  ArticlesDto,
  ArticlesResponseDto,
  PurchaseArticleCustomerInput,
  PurchaseArticleOutputDto,
  UserArticlePurchaseDto,
} from '../responses'

export interface sourceMap {
  title: string
  key: string
}

@Injectable({
  providedIn: 'root',
})
export class ArticlesService {
  private readonly apiService = inject(ApiService)
  private readonly apollo = inject(Apollo)

  getArticlesByEditionId(
    editionId: number,
    options: ArticleOptions = new ArticleOptions()
  ): Observable<ArticlesDto[]> {
    // Ensure options has fetchDetails set to true explicitly
    options.fetchDetails = true
    options.limit = options.limit || -1

    return this.apiService.getArticlesByEditionId(editionId, options).pipe(
      map(result => {
        // Handle the case where we have data even with errors
        const docs = result.data?.Articles.docs ?? []
        if (docs.length > 0) {
          return docs
        }

        // If we have errors but no data, throw an error
        if (result.error) {
          throw new Error(
            `GraphQL errors for edition ${String(editionId)}: ${result.error.message}`
          )
        }

        // If we have no data and no errors, return an empty array
        return []
      }),
      catchError(() => {
        // Always return an empty array to avoid breaking the UI
        return of([])
      })
    )
  }

  getArticleIdByOldId(oldArticleId: number): Observable<number> {
    return this.apiService.getArticleIdByOldId(oldArticleId).pipe(
      map(result => {
        const docs = result.data?.Articles.docs ?? []
        if (!docs.length) {
          throw new Error(`Article with ID ${String(oldArticleId)} not found`)
        }
        return docs[0].id
      }),
      catchError((error: unknown) => {
        console.error(
          `Error fetching article with ID ${String(oldArticleId)}:`,
          error
        )
        return throwError(
          () => new Error(`Failed to load article. Please try again later.`)
        )
      })
    )
  }

  getArticleIdBySeneId(seneArticleId: number): Observable<number> {
    return this.apiService.getArticleIdBySeneId(seneArticleId).pipe(
      map(result => {
        const docs = result.data?.Articles.docs ?? []
        if (!docs.length) {
          throw new Error(
            `Article with sene_id ${String(seneArticleId)} not found`
          )
        }
        return docs[0].id
      }),
      catchError((error: unknown) => {
        console.error(
          `Error fetching article with sene_id ${String(seneArticleId)}:`,
          error
        )
        return throwError(
          () => new Error(`Failed to load article. Please try again later.`)
        )
      })
    )
  }

  getArticlesByAuthorId(
    authorId: number,
    page: number,
    limit: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<{ articles: ArticlesDto[]; total: number }> {
    return this.apiService
      .getArticlesByAuthorId(authorId, page, limit, fetchPolicy)
      .pipe(
        map(result => {
          if (result.error) {
            throw new Error(
              `GraphQL errors for author ${String(authorId)}: ${result.error.message}`
            )
          }
          return {
            articles: result.data.Articles.docs,
            total: result.data.Articles.totalDocs,
          }
        }),
        catchError(() => {
          return of({ articles: [], total: 0 })
        })
      )
  }

  getArticleDetails(
    articleId: number,
    fetchPolicy: FetchPolicy = 'cache-first'
  ): Observable<ArticleDetailsDto['Article'] | undefined> {
    return this.apiService.getArticleDetails(articleId, fetchPolicy).pipe(
      map(result => {
        return result.data.Article
      }),
      catchError((error: unknown) => {
        console.error(
          `Error fetching article details for ID ${String(articleId)}:`,
          error
        )
        return throwError(
          () =>
            new Error(
              `Failed to load article details. Please try again later.`,
              { cause: error }
            )
        )
      })
    )
  }

  purchaseArticle(
    articleId: number,
    customer: PurchaseArticleCustomerInput,
    options?: ArticlePurchaseOptions | null
  ): Observable<PurchaseArticleOutputDto> {
    return this.apiService.purchaseArticle(articleId, customer, options).pipe(
      map(result => {
        const purchase = result.data?.purchaseArticle
        if (!purchase) {
          throw new Error('Failed to create article purchase')
        }

        return purchase
      }),
      catchError((error: unknown) => {
        console.error(
          `Error creating article purchase for article ${String(articleId)}:`,
          error
        )
        return throwError(
          () =>
            new Error(
              'Failed to start article purchase. Please try again later.'
            )
        )
      })
    )
  }

  getUserArticlePurchaseById(
    purchaseId: number,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<UserArticlePurchaseDto | null> {
    return this.apiService
      .getUserArticlePurchaseById(purchaseId, fetchPolicy)
      .pipe(
        map(result => result.data.UserArticlePurchase ?? null),
        catchError((error: unknown) => {
          console.error(
            `Error fetching user article purchase for ID ${String(purchaseId)}:`,
            error
          )
          return throwError(
            () =>
              new Error(
                'Failed to load article purchase status. Please try again later.'
              )
          )
        })
      )
  }

  getArticleIndex(articleId: number): Observable<IndexItem[]> {
    return this.apiService.getArticleIndex(articleId).pipe(
      map(result =>
        result.data.articleIndex.map((item, i) => ({
          title: item.title,
          level: item.level,
          link: `heading-${String(i)}`,
        }))
      ),
      catchError(() => {
        return of([])
      })
    )
  }

  getTotalArticlesCount(): Observable<number> {
    return this.apiService.getTotalArticlesCount().pipe(
      map(result => {
        if (result.error) {
          throw new Error(`GraphQL errors: ${result.error.message}`)
        }
        return result.data.countArticles.totalDocs
      }),
      catchError(() => {
        return of(0)
      })
    )
  }

  searchArticles(
    params: ArticleSearchParams,
    sorting: ArticleSearchSorting = [{ published_at: 'desc' }]
  ): Observable<{
    articles: ArticlesDto[]
    totalDocs: number
    page: number
    totalPages: number
  }> {
    const whereClause = this.buildWhereClause(params)
    const serializedSort = this.serializeSort(sorting)

    return this.executeSearchQuery(
      whereClause,
      params.page ?? 1,
      params.limit ?? 10,
      serializedSort
    )
  }

  private buildWhereClause(
    params: ArticleSearchParams
  ): ArticleSearchWhereClause {
    const conditions: ArticleSearchWhereConditions[] = []

    if (params.searchTerm) {
      const trimmedSearchTerm = params.searchTerm.trim()
      conditions.push({
        OR: [
          { title: { contains: trimmedSearchTerm } },
          { keywords__title: { contains: trimmedSearchTerm } },
          { categories__title: { contains: trimmedSearchTerm } },
        ],
      })
    }

    if (params.dateRange) {
      conditions.push({
        published_at: { greater_than_equal: params.dateRange.start },
      })
      conditions.push({
        published_at: { less_than_equal: params.dateRange.end },
      })
    }

    if (params.articleTypes?.length) {
      conditions.push({
        OR: params.articleTypes.map(type => ({
          article_type: {
            equals: {
              name: type,
              title: ARTICLE_TYPE_BACKEND_TITLES[type as ArticleType] ?? type,
            },
          },
        })),
      })
    }

    if (params.authorIds?.length) {
      conditions.push({
        OR: [
          { users__authors: { in: params.authorIds } },
          { users__creators: { in: params.authorIds } },
        ],
      })
    }

    if (params.magazineIds?.length) {
      conditions.push({ magazine: { in: params.magazineIds } })
    }

    return { AND: conditions }
  }

  private serializeSort(sorting: ArticleSearchSorting): string {
    return sorting
      .map(param => {
        const [key, value] = Object.entries(param)[0]
        return value === 'desc' ? `-${key}` : key
      })
      .join(',')
  }

  private executeSearchQuery(
    whereClause: ArticleSearchWhereClause,
    page: number,
    limit: number,
    sort: string
  ): Observable<{
    articles: ArticlesDto[]
    totalDocs: number
    page: number
    totalPages: number
  }> {
    return this.apollo
      .watchQuery<ArticlesResponseDto>({
        query: SEARCH_ARTICLES,
        variables: {
          whereClause,
          page,
          limit,
          sort,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        completeDataOrThrow(),
        map(result => {
          if (!result.data.Articles.docs.length) {
            return { articles: [], totalDocs: 0, page: 1, totalPages: 1 }
          }

          return {
            articles: result.data.Articles.docs,
            totalDocs: result.data.Articles.totalDocs,
            page: result.data.Articles.page,
            totalPages: result.data.Articles.totalPages,
          }
        })
      )
  }

  getArticlesByMagazineId(
    magazineId: number,
    limit = 10,
    page = 1,
    searchTerm: string,
    rulingDateRange: DateRange | null,
    sources: Set<string> | null,
    articleTypes: Set<string> | null,
    sort?: SortParameter
  ): Observable<{
    articles: ArticlesDto[]
    totalDocs: number
    page: number
    totalPages: number
  }> {
    return this.apiService
      .getArticlesByMagazineId(
        magazineId,
        page,
        limit,
        searchTerm,
        rulingDateRange,
        sources,
        articleTypes,
        sort
      )
      .pipe(
        map(result => {
          if (result.error) {
            throw new Error(
              `GraphQL errors for magazine ${String(magazineId)}: ${result.error.message}`
            )
          }

          return {
            articles: result.data.Articles.docs,
            totalDocs: result.data.Articles.totalDocs,
            page: result.data.Articles.page,
            totalPages: result.data.Articles.totalPages,
          }
        }),
        catchError((error: unknown) => {
          // Log the error and transform it to a more user-friendly format
          console.error('Error fetching articles by magazine ID:', error)
          return throwError(
            () => new Error('Failed to load articles. Please try again later.')
          )
        })
      )
  }

  /**
   * Downloads an article PDF by article ID
   *
   * @param articleId The ID of the article
   * @returns An Observable that resolves to a Blob containing the PDF data
   */
  downloadArticle(articleId: number): Observable<Blob> {
    return this.apiService.downloadArticle(articleId)
  }

  getSelectionAndEssenceSources(
    selectionAndEssenceId: number
  ): Observable<Set<sourceMap>> {
    return this.apiService
      .getSelectionAndEssenceSources(selectionAndEssenceId)
      .pipe(
        map(result => {
          if (result.error) {
            throw new Error(result.error.message)
          }

          const sources = result.data.selectionAndEssenceSources
          return new Set(sources)
        }),
        catchError((error: unknown) => {
          console.error('Error fetching selection and essence sources:', error)
          return of(new Set<sourceMap>())
        })
      )
  }
}
